// The seller's closing page: "Försäljningen är genomförd" — the Lovable design's DealClosing
// (components/completion/DealClosing.tsx), seller side, on the real backend.
//
// Not a success screen, and not a retelling of the journey the reader has just walked. It answers
// what the reader needs once the sale is done: the sale is done (kicker, H1), with whom for what
// (identity band, one-sentence lead), what still goes on without them (the change of ownership),
// where the documents are, and one door onward. The signing, the payment and their dates live on
// the transaction record; this page is written in the record's language — the same eyebrow, the
// same attributed, dated facts, the same document names — without being the record.
//
// TRUTH. Every row is something a party stated, attributed. The change of ownership is only ever
// REPORTED by the seller; Transportstyrelsen registers the owner and Klargo never hears back.
// Nothing here says the seller's insurance has ended: Klargo neither asks their insurer anything
// nor hears from it.
//
// WHERE THE FACTS COME FROM. The names, the price, the registration number and the moment the
// sale was completed come from the deal itself (ContractOwnership/GetState — the completion is
// the seller's own statement, recorded in the deal's log). The vehicle's name and the signing date
// come from the signed contract, read the way the transaction record reads them, so the two pages
// cannot describe different deals. The receipt is the one document with no file of its own in this
// backend; like the record, it opens the signed contract.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusDot } from "../StatusMark";
import { DocumentMark } from "../icons";
import ContractPreviewModal from "../ContractPreviewModal";
import PaperPreviewModal from "../PaperPreviewModal";
import ApiCall from "../../../../Apicall/ApiCall";
import { buildReceiptDocument } from "../../../lib/dealReceipt";
import { useLanguage } from "../../layout/LanguageSwitcher";
import { formatDateLocalized, formatRegNumber } from "../../../lib/format";
import { formatPersonName } from "../../../lib/formatName";
import { DEAL_HOME_PATH } from "../../../lib/dealSession";
import { DealIdentityBand } from "../payment/PaymentParts";
import { H1_CLASS, LEAD_CLASS, META_CLASS, OWNERSHIP_CONTENT_MAX } from "./OwnershipParts";

/** Where "View the transaction" goes: the permanent record of this deal. */
export const recordPath = (contractId) => `/affarshistorik/${contractId}`;

// The vehicle's name exactly as the transaction record states it.
export const closingVehicleName = (deal) =>
  [deal?.carInfoTradeName || deal?.carInfoVehicleDesignation || "", deal?.carInfoYearModel || ""]
    .join(" ")
    .trim() || (deal?.carInfoRegistrationNo ? formatRegNumber(deal.carInfoRegistrationNo) : "");

// The later of the two signatures, as the record states it.
const signedEventDate = (deal) =>
  deal?.sellerSignedAt && deal?.buyerSignedAt ? [deal.sellerSignedAt, deal.buyerSignedAt].sort().slice(-1)[0] : null;

/**
 * One way into a retained document: [mark] title / what happened to it · when / "Visa avtalet →".
 * The whole entry is ONE button that opens the document; the written verb is how the reader knows
 * that, so no icon-only control is needed. Nothing here downloads — the viewer carries that.
 */
function DocumentEntry({ title, meta, openLabel, ariaLabel, onOpen, testId }) {
  return (
    <div data-testid="document-entry" className="-mx-2.5">
      <button type="button" data-testid={testId} aria-label={ariaLabel} onClick={onOpen}
        className="group flex w-full min-w-0 items-start gap-[13px] rounded-[10px] px-2.5 py-3 text-left transition-colors hover:bg-[rgba(20,36,46,.045)]">
        <span className="flex w-[27px] flex-none justify-center pt-[2px]">
          <DocumentMark width={18} height={21} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold leading-[1.3] text-ds-ink">{title}</span>
          <span className="ds-meta mt-[2px] block font-normal leading-[1.4]">{meta}</span>
          <span className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold leading-[1.3] text-ds-ink transition-colors group-hover:text-ds-green">
            <span className="ds-underline">{openLabel}</span>
            <span aria-hidden="true">→</span>
          </span>
        </span>
      </button>
    </div>
  );
}

export default function DealClosing({ state, deal, contractId, onNavigate }) {
  const { t } = useTranslation();
  const { t: tp, languageCode } = useLanguage();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);

  // When the seller confirmed the payment. The receipt's payment line speaks from that event's own
  // timestamp in the deal's log, or not at all - a log that cannot be read just makes it shorter.
  const [paidAt, setPaidAt] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiCall({ url: "/DealLog/GetByContract", method: "GET", params: { Id: contractId } });
        const rows = res?.data?.result || res?.result || [];
        const row = Array.isArray(rows) ? rows.find((r) => r.eventType === "payment_confirmed") : null;
        if (!cancelled) setPaidAt(row?.creationTime || null);
      } catch (_) {
        /* no payment line on the receipt */
      }
    })();
    return () => { cancelled = true; };
  }, [contractId]);

  const date = (iso) => formatDateLocalized(iso, languageCode);
  // Display only: legacy accounts may hold BankID-style CAPS names.
  const buyerName = formatPersonName(state.buyerName) || t("ownership.buyerFallback");
  const vehicle = closingVehicleName(deal);
  const completedAt = state.reportedAtUtc;
  const signedAt = signedEventDate(deal);

  // Only what is still in motion. The signed contract, the payment and the report are behind the
  // reader — the rail's ticks say so and the record keeps them dated — so the ledger holds the ONE
  // process that goes on without them: Transportstyrelsen registers the owner. It reads as the
  // vehicle page's does: reported, never registered.
  const rows = [
    { key: "ownership", label: t("completion.row.ownership"), text: t("completion.reportedByYou"), note: t("ownership.seller.reported.meta") },
  ];

  const contractTitle = tp("ds.document.purchaseContract");
  const receiptTitle = tp("ds.document.receipt");

  // The receipt as the paper it opens as: the vehicle, price, parties and each event's date from the
  // deal; the reference derived from its id; the explanatory copy constant.
  const receipt = receiptOpen
    ? buildReceiptDocument({
      contractId,
      registrationNo: state.registrationNo,
      vehicleName: vehicle,
      amount: state.amount,
      sellerName: formatPersonName(state.sellerName) || t("payment.sellerFallback"),
      buyerName,
      signedAt,
      paidAt,
      transferredAt: completedAt,
      tp,
      language: languageCode,
    })
    : null;

  const lower = (text) => text.charAt(0).toLowerCase() + text.slice(1);
  const receiptCreated = tp("ds.record.document.created").replace("{date}", date(completedAt).replace(/ /g, " "));

  return (
    <>
      <div className="mx-auto w-full pb-8" style={{ maxWidth: OWNERSHIP_CONTENT_MAX }}
        data-testid="deal-closing" data-role="seller">
        <DealIdentityBand plate={state.registrationNo} amount={state.amount} language={languageCode} t={t} />

        {/* The record's own eyebrow, word for word: the closing moment and the permanent record say
            the same thing about the same deal. */}
        <div className="mb-2 flex items-center gap-2" data-testid="closing-kicker">
          <StatusDot tone="green" />
          <span className="text-[13px] font-semibold tracking-[0.02em] text-ds-muted">
            {tp("ds.record.eyebrow.completed")}{completedAt ? ` · ${date(completedAt)}` : ""}
          </span>
        </div>
        <h1 className={H1_CLASS}>{t("completion.seller.title")}</h1>
        <p className={LEAD_CLASS} data-testid="closing-lead">
          {t("completion.seller.lead", { vehicle: vehicle || formatRegNumber(state.registrationNo), buyer: buyerName })}
        </p>

        {/* What is still in motion — ruled and attributed. No ticks: the rail already says every
            stage is complete. */}
        <dl className="m-0 mt-6 border-t border-[rgba(20,36,46,.25)]" data-testid="closing-ledger">
          {rows.map((row) => (
            <div key={row.key} data-testid={`closing-row-${row.key}`}
              className="border-b border-ds-rule-row py-3 sm:grid sm:grid-cols-[9.5rem_1fr] sm:gap-x-6 sm:py-3.5">
              <dt className="ds-label">{row.label}</dt>
              <dd className="m-0 mt-0.5 text-[15px] font-medium leading-[1.45] text-ds-ink sm:mt-0">
                {row.text}
                {row.note && <span className={`${META_CLASS} mt-0.5 block font-normal`}>{row.note}</span>}
              </dd>
            </div>
          ))}
        </dl>
        {/* The seller's one boundary Klargo cannot close for them: their own insurer. */}
        <p className={`${META_CLASS} mt-3.5`} data-testid="closing-note">{t("completion.seller.noteInsurance")}</p>

        {/* The deal's retained documents, entered exactly as on the record: one entry, one verb,
            nothing that pretends to download. */}
        <section className="mt-8" aria-labelledby="closing-documents-title" data-testid="closing-documents">
          <h2 id="closing-documents-title" className="m-0 mb-1 text-[15px] font-semibold text-ds-ink">
            {tp("ds.record.documents.title")}
          </h2>
          <div className="border-t border-[rgba(20,36,46,.25)]">
            <div className="border-b border-ds-rule-row" data-testid="closing-contract">
              <DocumentEntry
                testId="closing-contract-open"
                title={contractTitle}
                meta={signedAt
                  ? `${tp("ds.record.contract.signedBoth")} · ${date(signedAt).replace(/ /g, " ")}`
                  : tp("ds.record.contract.signedBoth")}
                openLabel={tp("ds.record.contract.view")}
                ariaLabel={tp("ds.document.contractAria").replace("{date}", date(signedAt))}
                onOpen={() => setPreviewOpen(true)}
              />
            </div>
            <div className="border-b border-ds-rule-row" data-testid="closing-receipt">
              <DocumentEntry
                testId="closing-receipt-open"
                title={receiptTitle}
                meta={`Klargo · ${receiptCreated}`}
                openLabel={tp("ds.document.view")}
                ariaLabel={tp("ds.document.aria").replace("{document}", lower(receiptTitle)).replace("{date}", date(completedAt))}
                onOpen={() => setReceiptOpen(true)}
              />
            </div>
          </div>
        </section>

        {/* One door onward: the durable home of what the seller now has. */}
        <div className="mt-8 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-7">
          <button type="button" onClick={() => onNavigate(recordPath(contractId))}
            className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[220px]" data-testid="closing-primary">
            {t("completion.seller.primary")}
          </button>
          <button type="button" onClick={() => onNavigate(DEAL_HOME_PATH)}
            className="ds-action-text min-h-[44px] justify-center" data-testid="closing-secondary">
            <span className="ds-underline">{t("completion.seller.secondary")}</span>
          </button>
        </div>
      </div>
      <ContractPreviewModal t={t} open={previewOpen} onClose={() => setPreviewOpen(false)} id={contractId} />
      {/* The receipt opens in the same document modal as the contract, as the paper it is. */}
      <PaperPreviewModal
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        title={receiptTitle}
        document={receipt}
        fileName={`Klargo-Receipt-${state.registrationNo || contractId}.pdf`}
        labels={{ download: t("actions.downloadPdf"), close: t("dealDetails.action.close"), pageOf: tp("contract.preview.pageOf") }}
      />
    </>
  );
}
